import React, { useState } from 'react';
import { Check, Star, Zap, Award, Microscope, School } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PopupExample from './WaitingListPopup';
import { toast, ToastContainer } from 'react-toastify';

const PricingCard = ({
    title,
    price,
    description,
    features,
    buttonText,
    isPopular,
    icon: Icon,
    onButtonClick,
    bestFor,
    pitch,
    goal
}) => (
    <div
        className={`relative flex flex-col h-full p-6 rounded-2xl transition-all duration-300 ${isPopular
            ? 'bg-blue-600 text-white shadow-xl scale-105 z-10'
            : 'bg-white text-gray-900 shadow-md hover:shadow-xl border border-blue-100 hover:border-blue-200'
            }`}
    >
        {isPopular && (
            <div className="absolute top-0 right-0 -mt-2 -mr-2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide bg-yellow-400 text-blue-900 shadow-sm">
                    Most Popular
                </span>
            </div>
        )}

        <div className="mb-4">
            <div className={`p-2 rounded-xl w-fit mb-3 ${isPopular ? 'bg-white/10' : 'bg-blue-50'}`}>
                <Icon className={`w-6 h-6 ${isPopular ? 'text-white' : 'text-blue-600'}`} />
            </div>
            <h3 className={`text-xl font-bold mb-1 ${isPopular ? 'text-white' : 'text-blue-900'}`}>{title}</h3>
            <p className={`text-xs mb-4 min-h-[2.5rem] ${isPopular ? 'text-blue-100' : 'text-gray-500'}`}>
                {description}
            </p>

            <div className="flex items-baseline mb-4">
                <span className="text-3xl font-bold tracking-tight">
                    {price === 'Custom' ? 'Custom' : <span dangerouslySetInnerHTML={{ __html: price }}></span>}
                </span>
                {price !== 'Free' && !price.includes('–') && (
                    <span className={`ml-1 text-xs font-medium ${isPopular ? 'text-blue-100' : 'text-gray-500'}`}>
                        {price.includes('one-time') ? '' : ''}
                    </span>
                )}
            </div>

            {/* Best For Section */}
            {(bestFor || goal) && (
                <div className={`mb-4 p-3 rounded-lg ${isPopular ? 'bg-white/10' : 'bg-gray-50'}`}>
                    {bestFor && (
                        <div className="text-xs mb-1">
                            <span className={`font-bold uppercase ${isPopular ? 'text-blue-100' : 'text-gray-400'}`}>Best For: </span>
                            <span className={`font-medium ${isPopular ? 'text-white' : 'text-gray-700'}`}>{bestFor}</span>
                        </div>
                    )}
                    {goal && (
                        <div className="text-xs">
                            <span className={`font-bold uppercase ${isPopular ? 'text-blue-100' : 'text-gray-400'}`}>Goal: </span>
                            <span className={`font-medium ${isPopular ? 'text-white' : 'text-gray-700'}`}>{goal}</span>
                        </div>
                    )}
                </div>
            )}

            {/* Pitch Section */}
            {pitch && (
                <div className="mb-4 text-sm italic font-medium">
                    " {pitch} "
                </div>
            )}
        </div>

        <div className="flex-grow">
            <div className={`w-full h-px mb-4 ${isPopular ? 'bg-blue-500' : 'bg-blue-100'}`} />
            <ul className="space-y-3 mb-6">
                {features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                        <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isPopular ? 'text-yellow-400' : 'text-blue-600'}`} />
                        <span className={`text-sm leading-tight ${isPopular ? 'text-blue-50' : 'text-gray-600'}`}>
                            {feature}
                        </span>
                    </li>
                ))}
            </ul>
        </div>

        <button
            onClick={onButtonClick}
            className={`w-full py-3 px-4 rounded-lg font-bold text-sm transition-all duration-200 transform hover:-translate-y-0.5 ${isPopular
                ? 'bg-white text-blue-600 hover:bg-blue-50 shadow-md'
                : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow-md'
                }`}
        >
            {buttonText}
        </button>
    </div>
);

const PricingPageStudent = () => {
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [pro, setPro] = useState("");

    const pricingData = [
        {
            title: 'Student Basic',
            price: 'Free',
            description: 'Practice & explore synthetic data.',
            icon: Star,
            features: [
                'Generate small datasets',
                'Ready-made templates (Q&A, classification)',
                'Community datasets',
                'Limited exports',
                'Community support'
            ],
            bestFor: 'learning, experiments, mini assignments',
            goal: 'onboarding only',
            buttonText: 'Start for Free',
            onButtonClick: () => navigate("/signin"),
            isPopular: false
        },
        {
            title: 'Custom Dataset',
            price: '$9 <span class="text-sm font-normal text-blue-200">(one-time)</span>',
            description: 'We prepare your project dataset for you.',
            icon: Zap, // Represents speed/done-for-you
            features: [
                'Customized dataset for your topic/domain',
                '1,000–5,000 rows (clean & balanced)',
                'Synthetic + augmented data',
                'CSV/JSON export',
                'Ready-to-train format',
                'Basic documentation',
                '48–72 hr delivery'
            ],
            bestFor: 'final-year projects, hackathons, internships',
            pitch: 'Skip weeks of data collection. Get your dataset ready fast.',
            buttonText: 'Order Custom Dataset',
            onButtonClick: () => {
                setPro("Custom Dataset");
                setIsOpen(true);
            },
            isPopular: true
        },
        {
            title: 'Certified Project',
            price: '$5 <span class="text-sm font-normal text-gray-500">(one-time)</span>',
            description: 'Validate your dataset & earn a certificate.',
            icon: Award, // Represents certification
            features: [
                'Dataset quality evaluation',
                'Class balance check',
                'Duplicate detection',
                'Data cleaning score',
                'Quality report (PDF)',
                'Verifiable certificate',
                'LinkedIn shareable badge'
            ],
            bestFor: 'resumes, placements, thesis submissions',
            pitch: 'Show recruiters your dataset meets industry standards.',
            buttonText: 'Get Certified',
            onButtonClick: () => {
                setPro("Certified Project");
                setIsOpen(true);
            },
            isPopular: false
        },
        {
            title: 'Research Pro',
            price: '$15–$25',
            description: 'For thesis & heavy research.',
            icon: Microscope,
            features: [
                'Larger datasets (10k–50k+ rows)',
                'Multiple submissions',
                'Expert/manual review',
                'Custom schema support',
                'Priority processing',
                'Storage'
            ],
            bestFor: 'PhD / serious research',
            buttonText: 'Contact Us',
            onButtonClick: () => {
                setPro("Research Pro");
                setIsOpen(true);
            },
            isPopular: false
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50 font-sans">
            <section className="py-20 px-4 bg-gradient-to-br from-indigo-50 via-blue-50 to-white overflow-hidden">
                <PopupExample hidden={isOpen} isOpen={isOpen} setIsOpen={setIsOpen} pro={pro} toast={toast} />

                <div className="max-w-7xl mx-auto relative">
                    <div className="text-center mb-16 relative z-10">
                        <div className="inline-block p-2 px-4 rounded-full bg-blue-100 text-blue-700 font-semibold text-sm mb-4">
                            <School className="inline w-4 h-4 mr-2 mb-0.5" />
                            Special Academic Pricing
                        </div>
                        <h2 className="text-4xl font-extrabold text-blue-900 mb-6 tracking-tight">
                            Supercharge Your Studies
                        </h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8 leading-relaxed">
                            Skip weeks of data collection. Get your projects done faster and better.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 items-start relative z-10 px-2 max-w-7xl mx-auto">
                        {pricingData.map((plan, index) => (
                            <div key={index} className="h-full">
                                <PricingCard {...plan} />
                            </div>
                        ))}
                    </div>

                    <div className="mt-16 text-center">
                        <p className="text-gray-600 text-base">
                            Need a custom plan for your entire university or lab? <button onClick={() => { setPro("University Plan"); setIsOpen(true); }} className="text-blue-600 font-bold hover:underline">Contact us</button>
                        </p>
                    </div>
                </div>
                <ToastContainer position="top-center" autoClose={3000} />
            </section>
        </div>
    );
};

export default PricingPageStudent;
